import React from 'react';
import { Box, Container, Divider, Grid, Link, Stack, Typography } from '@mui/material';

interface FooterSection {
  title: string;
  items: string[];
}

interface SocialItem {
  label: string;
  icon: string;
  href: string;
  containerSx?: {
    minWidth?: number;
    height?: number;
    px?: number;
    borderRadius?: number | string;
  };
  imageSx?: {
    width?: number | string;
    height?: number | string;
  };
}

const footerSections: FooterSection[] = [
  {
    title: 'INVENTORY',
    items: [
      'Shop New Cadillac Vehicles',
      'Browse Certified Pre-Owned',
      'Explore Pre-Owned Inventory',
      'Vehicles Under $25,000',
    ],
  },
  {
    title: 'SERVICE',
    items: [
      'Schedule Service Appointment',
      'Cadillac Service Center',
      'Order Genuine Cadillac Parts',
      'Tire & Maintenance Specials',
    ],
  },
  {
    title: 'ABOUT',
    items: [
      'About Sterling Cadillac',
      'Luxury Car Dealer in Tampa, FL',
      'Contact Our Team',
      'Careers & Opportunities',
      'Customer Reviews',
      'Serving Tampa, St. Petersburg & Clearwater',
    ],
  },
];

const socialItems: SocialItem[] = [
  {
    label: 'Facebook',
    icon: '/static/images/facebook.png',
    href: '#',
    containerSx: { minWidth: 40, height: 40, px: 0.5, borderRadius: '50%' },
    imageSx: { width: 24, height: 24 },
  },
  {
    label: 'Instagram',
    icon: '/static/images/instagram.png',
    href: '#',
    containerSx: { minWidth: 40, height: 40, px: 0.5, borderRadius: 1.75 },
    imageSx: { width: 24, height: 24 },
  },
  {
    label: 'X',
    icon: '/static/images/twitter_logo_white.png',
    href: '#',
    containerSx: { minWidth: 40, height: 40, px: 0.5, borderRadius: '50%' },
    imageSx: { width: 24, height: 24 },
  },
  {
    label: 'YouTube',
    icon: '/static/images/youtube_white_text.svg',
    href: '#',
    containerSx: { minWidth: 128, height: 40, px: 0, borderRadius: 1.75 },
    imageSx: { width: '100%', height: 28 },
  },
];

const Footer: React.FC = () => {
  return (
    <Box
      component='footer'
      sx={{
        bgcolor: '#000000',
        color: '#ffffff',
        pt: { xs: 5, sm: 6, md: 7 },
        pb: { xs: 2, sm: 2.5 },
      }}
    >
      <Container maxWidth='lg'>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.35)', mb: { xs: 4, md: 5 } }} />

        <Stack spacing={{ xs: 4, md: 5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component='img'
              src='/static/images/sterling_cadillac_logo.png'
              alt='Sterling Cadillac logo'
              sx={{
                width: { xs: 220, sm: 280, md: 320 },
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain',
                filter: 'grayscale(1) brightness(1.15)',
              }}
            />
          </Box>

          <Grid
            container
            spacing={{ xs: 3, sm: 4, md: 6 }}
            sx={{ justifyContent: { xs: 'center', md: 'space-between' } }}
          >
            {footerSections.map((section) => (
              <Grid key={section.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Stack
                  spacing={1}
                  sx={{
                    maxWidth: { xs: '100%', md: 320 },
                    mx: { xs: 'auto', md: 0 },
                    textAlign: { xs: 'left', sm: 'left' },
                  }}
                >
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.05rem' },
                      fontWeight: 500,
                      letterSpacing: 0.2,
                    }}
                  >
                    {section.title}
                  </Typography>

                  <Box component='ul' sx={{ m: 0, pl: 2.25 }}>
                    {section.items.map((item) => (
                      <Box
                        component='li'
                        key={`${section.title}-${item}`}
                        sx={{
                          mb: 0.6,
                          '&::marker': {
                            color: '#ffffff',
                            fontSize: '0.9rem',
                          },
                        }}
                      >
                        <Link
                          href='#'
                          underline='none'
                          color='inherit'
                          sx={{
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            lineHeight: 1.35,
                            transition: 'opacity 160ms ease',
                            '&:hover': { opacity: 0.75 },
                          }}
                        >
                          {item}
                        </Link>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>

          <Stack spacing={1.25} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Typography
              variant='h6'
              sx={{
                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                fontWeight: 500,
                letterSpacing: 0.2,
              }}
            >
              CONNECT WITH US
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: 'rgba(255,255,255,0.92)',
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                maxWidth: 680,
                px: 1,
              }}
            >
              Follow us for exclusive offers, new arrivals, and event updates.
            </Typography>

            <Stack
              direction='row'
              spacing={{ xs: 1, sm: 1.5 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                pt: 1,
              }}
            >
              {socialItems.map((item) => (
                <Box
                  key={item.label}
                  component='a'
                  href={item.href}
                  aria-label={`${item.label} social link`}
                  sx={{
                    minWidth: item.containerSx?.minWidth ?? 40,
                    height: item.containerSx?.height ?? 40,
                    px: item.containerSx?.px ?? 0.5,
                    borderRadius: item.containerSx?.borderRadius ?? '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    textDecoration: 'none',
                    transition: 'opacity 160ms ease',
                    '&:hover': { opacity: 0.85 },
                  }}
                >
                  <Box
                    component='img'
                    src={item.icon}
                    alt={item.label}
                    sx={{
                      width: item.imageSx?.width ?? 24,
                      height: item.imageSx?.height ?? 24,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: { xs: 4, md: 5 } }} />
      </Container>
    </Box>
  );
};

export default Footer;
